package com.evoapps.evoshift.domain.budget

import com.evoapps.evoshift.domain.model.FinancialEngagement
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.ShiftFinancials
import com.evoapps.evoshift.domain.model.WorkplaceSettings
import com.evoapps.evoshift.domain.model.workerId
import com.evoapps.evoshift.domain.repository.UserRepository
import com.evoapps.evoshift.domain.usecase.CalculateShiftCostUseCase
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.coroutineScope

/*
 * Shifts whose cost snapshot (shift_financials) is missing or stale. A snapshot is written when a
 * shift is assigned, edited or the month is refreshed, by someone allowed to price (MANAGE_PAY);
 * until then (a shift created for someone, handed to someone else, priced by a manager who may
 * not read rates) it has none, or one of the previous person. The labour cost used to drop such a
 * shift altogether, its cost AND its hours, so a day could show 29 planned hours for 345 zł.
 * Now: whoever may read the person's pay terms prices it on the fly, exactly as its snapshot will
 * be priced (CalculateShiftCostUseCase.price); a person without pay terms at the workplace leaves
 * it unpriced, and the screens say so, naming them.
 */
object MissingShiftCosts {
    /** [snapshot] still prices [shift]: written for whoever works it (or before snapshots named anyone). */
    fun isUsable(snapshot: ShiftFinancials?, shift: Shift): Boolean =
        snapshot != null && (snapshot.userId == null || snapshot.userId == shift.workerId)

    /**
     * The paid shifts among [shifts] someone works that have no usable snapshot in [snapshots]
     * ([isPaid]: an unpaid owner's shifts are not paid work; shifts marked not worked do not count).
     */
    fun unpriced(shifts: List<Shift>, snapshots: Map<String, ShiftFinancials>, isPaid: (String) -> Boolean): List<Shift> =
        shifts.filter { shift ->
            val worker = shift.workerId
            worker != null && !shift.notWorked && isPaid(worker) && !isUsable(snapshots[shift.id], shift)
        }

    /**
     * Snapshots for [missing] shifts priced on the fly from their people's pay terms ([engagements]
     * by user id), as CalculateShiftCostUseCase will store them; shifts of people without terms are
     * left out (still unpriced). [monthShifts]: the month, for each person's monthly hours.
     */
    fun price(
        missing: List<Shift>,
        monthShifts: List<Shift>,
        engagements: Map<String, FinancialEngagement>,
        settings: WorkplaceSettings
    ): List<ShiftFinancials> {
        val hoursByUser = monthShifts.filter { it.workerId != null }.groupBy { it.workerId!! }
            .mapValues { (_, theirs) -> theirs.sumOf(ShiftCostEstimate::hoursOf) }
        return missing.mapNotNull { shift ->
            val worker = shift.workerId ?: return@mapNotNull null
            val engagement = engagements[worker] ?: return@mapNotNull null
            runCatching { CalculateShiftCostUseCase.price(shift, worker, engagement, settings, hoursByUser[worker] ?: 0.0) }.getOrNull()
        }
    }

    /**
     * SH-147: [ownerId] when [missing] holds their shifts and they have no pay terms in
     * [engagements]: working without a wage is normal for the owner, so their shifts cost nothing
     * and are not "unpriced" (no warning names them). Owners with pay terms are priced as anyone.
     */
    fun ownerWithoutTerms(ownerId: String?, missing: List<Shift>, engagements: Map<String, FinancialEngagement>): Set<String> =
        if (ownerId != null && ownerId !in engagements && missing.any { it.workerId == ownerId }) setOf(ownerId) else emptySet()

    /** [userIds]' pay terms at [workplaceId], read side by side; people whose terms cannot be read are left out. */
    suspend fun engagementsOf(userRepository: UserRepository, workplaceId: String, userIds: Collection<String>): Map<String, FinancialEngagement> =
        coroutineScope {
            userIds.distinct().map { uid ->
                async { userRepository.getSensitiveUserData(uid).getOrNull()?.financialEngagements?.get(workplaceId)?.let { uid to it } }
            }.awaitAll().filterNotNull().toMap()
        }

    /**
     * [snapshots] completed: the usable ones as they are, plus the [missing] shifts priced from
     * [engagements] (replacing a stale snapshot of someone else).
     */
    fun completed(
        snapshots: List<ShiftFinancials>,
        missing: List<Shift>,
        monthShifts: List<Shift>,
        engagements: Map<String, FinancialEngagement>,
        settings: WorkplaceSettings
    ): List<ShiftFinancials> {
        val priced = price(missing, monthShifts, engagements, settings)
        val replaced = priced.map { it.shiftId }.toSet()
        return snapshots.filterNot { it.shiftId in replaced } + priced
    }
}
